// @ts-nocheck
/**
 * ドラフトシステム - 自動保存フック
 */

import { useEffect, useRef, useCallback } from 'react';
import { saveDraftSession } from '../systems/draftStorage';

/**
 * 自動保存フック
 * @param {Object} session - ドラフトセッション
 * @param {Object} options - オプション
 * @param {number} options.debounceMs - デバウンス時間（ミリ秒）
 * @param {boolean} options.enabled - 有効フラグ
 * @returns {Object} 保存操作
 */
export const useAutoDraftSave = (session, options = {}) => {
  const {
    debounceMs = 500,
    enabled = true,
  } = options;

  const saveTimeoutRef = useRef(null);
  const lastSavedRef = useRef(null);

  // ========================================
  // 保存処理
  // ========================================

  /**
   * 即座に保存
   */
  const saveNow = useCallback(() => {
    if (!session) return false;

    try {
      const success = saveDraftSession(session);
      if (success) {
        lastSavedRef.current = new Date().toISOString();
        console.log('[AutoSave] Saved at', lastSavedRef.current);
      }
      return success;
    } catch (e) {
      console.error('[AutoSave] Save failed:', e);
      return false;
    }
  }, [session]);

  /**
   * デバウンス保存
   */
  const scheduleSave = useCallback(() => {
    if (!enabled || !session) return;

    // 既存のタイマーをクリア
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // 新しいタイマーをセット
    saveTimeoutRef.current = setTimeout(() => {
      saveNow();
    }, debounceMs);
  }, [session, enabled, debounceMs, saveNow]);

  // ========================================
  // 自動保存
  // ========================================

  /**
   * セッション変更時に自動保存
   */
  useEffect(() => {
    if (!enabled || !session) return;

    // セッションIDが変わったら即座に保存
    if (lastSavedRef.current === null) {
      saveNow();
      return;
    }

    // それ以外はデバウンス保存
    scheduleSave();

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [session, enabled, saveNow, scheduleSave]);

  /**
   * ブラウザ終了時に保存
   */
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = () => {
      if (session) {
        saveNow();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [session, enabled, saveNow]);

  /**
   * 可視性変更時に保存（タブ切り替え等）
   */
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && session) {
        saveNow();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [session, enabled, saveNow]);

  // ========================================
  // 返却値
  // ========================================
  return {
    saveNow,
    scheduleSave,
    lastSaved: lastSavedRef.current,
  };
};

export default useAutoDraftSave;
